/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useMemo } from 'react';
import { CategoryPartList } from '../../CategoryPartList';
import { useCategoryResults } from '../../../api/getCategoryResults';
import { useProductDataTopNavMenuV2 } from '@/features/topNav/api/getProductDataTopNavMenuV2';
import { useLabels } from '@/hooks/useLabels';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { usePageType } from '@/features/category/api/getPageType';

const labelMap = {
  lblShopByCategory: 'label_shop_by_category',
};

export const CMSShopByCategory = () => {
  const { acesEnabled } = useAcesFlags();
  const { data: pageTypeData } = usePageType();
  const { lblShopByCategory } = useLabels(labelMap);

  const hasCatalogPath = Boolean(pageTypeData?.categoryRepositoryIdPath);
  const hasMakeModelYearPath = Boolean(pageTypeData?.makeModelYearPath);

  const shouldUseV2MenuData = acesEnabled && pageTypeData?.pageType === 'CategoryGrouping';

  const isPartsPage = pageTypeData?.catalogId === '0-0';

  const { data: categoryDataV1 } = useCategoryResults({ enabled: !shouldUseV2MenuData });
  const { data: categoryDataV2, isLoading: isLoadingV2 } = useProductDataTopNavMenuV2({
    level: -1,
    categoryIdPath: pageTypeData?.categoryRepositoryIdPath,
    isEnabled: shouldUseV2MenuData && (hasCatalogPath || hasMakeModelYearPath),
  });

  const categoryData = useMemo(() => {
    if (shouldUseV2MenuData) {
      if (isLoadingV2) {
        return undefined;
      }
      return categoryDataV2;
    }
    // Use V1 data when V2 is not required (parts-pro flow)
    return categoryDataV1;
  }, [shouldUseV2MenuData, categoryDataV1, categoryDataV2, isLoadingV2]);

  const hasNoCategories = useMemo(() => {
    if (!categoryData) {
      return false;
    }
    return shouldUseV2MenuData
      ? !categoryData?.rootCategories?.[0]?.shopByCategory?.length
      : !categoryData?.shopByCategory?.length;
  }, [categoryData, shouldUseV2MenuData]);

  if (!categoryData || hasNoCategories) {
    return null;
  }

  return (
    <CategoryPartList
      data={{
        name: lblShopByCategory,
        shopByCategory: shouldUseV2MenuData
          ? isPartsPage
            ? categoryData?.rootCategories
            : categoryData.rootCategories?.[0].shopByCategory
          : categoryData?.shopByCategory,
      }}
    />
  );
};
